import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { sortFilterCategories } from '@zero/shared/util-helper';
import { CustomEntryQuotesRequest, InstrumentType, PortfolioPerformance, Position, PositionNote, Quote } from '@zero/shared/util-models';
import { isEmpty } from 'lodash-es';
import { catchError, filter, map, of, retry, switchMap, tap } from 'rxjs';
import { AuthStateFacade } from '../../state/auth/auth-state.facade';
import { StoreErrorHandler } from '../base/effects.base';
import { positionsActions, positionsApiActions } from './positions.actions';
import { PositionsInfrastructure } from './positions.infrastructure';
import { positionsFeature } from './positions.reducers';
import { selectPositionByIsin } from './positions.selectors';

@Injectable({ providedIn: 'root' })
export class PositionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly positionsInfrastructure = inject(PositionsInfrastructure);
  private readonly pushQuotes = inject(PushQuoteService);
  private readonly storeErrorHandler = inject(StoreErrorHandler);
  private readonly authState = inject(AuthStateFacade);

  public loadPositions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.loadPositions),
      filter(() => !!this.authState.customerId),
      concatLatestFrom(() => this.store.select(positionsFeature.selectPositionsInitialLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      tap(() => {
        // eslint-disable-next-line @ngrx/no-dispatch-in-effects, @ngrx/avoid-dispatching-multiple-actions-sequentially
        this.store.dispatch(positionsActions.setLoading({ loading: true }));
        // eslint-disable-next-line @ngrx/no-dispatch-in-effects, @ngrx/avoid-dispatching-multiple-actions-sequentially
        this.store.dispatch(positionsActions.setPerformanceCalculated({ calculated: false }));
      }),
      switchMap(() =>
        this.positionsInfrastructure.getPositions().pipe(
          retry(2),
          map(({ instruments, list }) => {
            const positions = list
              .map((value) => calculatePositionPerformance(value))
              .map((value) => {
                const instrument = instruments.find((instrument) => instrument.isin === value.isin);
                if (instrument) {
                  value.historicQuote =
                    instrument.historicQuotes?.length > 0 ? instrument.historicQuotes[instrument.historicQuotes.length - 1] : null;
                  value.endfaellig = instrument.endfaellig;
                  value.knockedOutFlag = instrument.knockedOutFlag;
                  value.assetName = value.assetName ?? instrument.longName;
                  value.longName = instrument.longName;
                  value.shortName = instrument.shortName;
                  value.type = instrument.type;
                  value.isBuyable = false;
                  value.isSellable = false;
                  value.isSavingsPlanEligible = instrument.sparplan;
                }
                return value;
              });
            return { positions };
          }),
          map(({ positions }) => positionsApiActions.loadPositionsSuccess({ positions })),
          catchError((error) => of(positionsApiActions.loadPositionsFailure({ error }))),
        ),
      ),
      catchError((error) => of(positionsApiActions.loadPositionsFailure({ error }))),
    );
  });

  public calculatePerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        positionsApiActions.loadPositionsSuccess,
        positionsActions.positionsPerformanceCalculated,
        positionsApiActions.updatePositionEntryQuoteSuccess,
      ),
      concatLatestFrom(() => this.store.select(positionsFeature.selectAll)),
      map(([{ type }, positions]) => {
        const validPositions = positions.filter((pos) => !pos.endfaellig && !pos.knockedOutFlag);

        const entryValue = validPositions.reduce<number>((acc, curr) => acc + curr.entryValue, 0);
        const currentValue = validPositions.reduce<number>((acc, curr) => acc + curr.currentValue, 0);
        const perfTodayAbs = validPositions.reduce<number>((acc, curr) => acc + curr.perfTodayAbs, 0);
        const perfTodayPct = currentValue !== 0 ? (currentValue / (currentValue - perfTodayAbs) - 1) * 100 : 0;
        const sumPerfPct = currentValue !== 0 ? (currentValue / entryValue - 1) * 100 : 0;
        const sumPerfAbs = currentValue - entryValue;

        const newPerformance = {
          entryValue,
          currentValue,
          perfTodayAbs,
          perfTodayPct,
          sumPerfPct,
          sumPerfAbs,
        } satisfies PortfolioPerformance;

        return positionsActions.calculatePortfolioPerformance({
          performance: newPerformance,
          quotesLoaded: type === positionsActions.positionsPerformanceCalculated.type,
        });
      }),
      catchError(() => of(positionsActions.calculatePortfolioPerformanceFailure())),
    );
  });

  public handleUserLogout$ = createEffect(() => {
    return this.authState.loggedIn$.pipe(
      filter((loggedIn) => !loggedIn),
      map(() => positionsActions.unsubscribeFromPositionQuotes()),
    );
  });

  public subscribeToQuotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.subscribeToPositionQuotes),
      switchMap(() => this.store.select(positionsFeature.selectIds)),
      filter((isins) => isins.length > 0),
      switchMap((isins) =>
        this.pushQuotes.subscribeMultiBuffered(isins as string[]).pipe(
          filter((quotes) => !isEmpty(quotes)),
          concatLatestFrom(() => this.store.select(positionsFeature.selectAll)),
          map(([quotes, positions]) => positions.map((position) => mapQuoteToPosition(quotes, position)).filter((pos) => pos)),
          map((positions) => positionsActions.positionsPerformanceCalculated({ positions })),
          catchError(() => of(positionsActions.positionQuotesSubscriptionFailure())),
        ),
      ),
      catchError(() => of(positionsActions.positionQuotesSubscriptionFailure())),
    );
  });

  public unsubscribeFromQuotes$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(positionsActions.unsubscribeFromPositionQuotes),
        concatLatestFrom(() => this.store.select(positionsFeature.selectIds)),
        filter(([, isins]) => isins.length > 0),
        tap(([, isins]) => {
          this.pushQuotes.unsubscribeMulti(isins as string[]);
        }),
      );
    },
    { dispatch: false },
  );

  public subscribeToPositionQuote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.subscribeToPositionQuote),
      switchMap(({ isin }) =>
        this.pushQuotes.subscribeMultiBuffered([isin]).pipe(
          filter((quotes) => !isEmpty(quotes)),
          concatLatestFrom(() => this.store.select(positionsFeature.selectAll)),
          map(([quotes, positions]) => {
            const newPositions = positions.map((position) => mapQuoteToPosition(quotes, position)).filter((pos) => pos);
            return newPositions;
          }),
          map((positions) => positionsActions.positionsPerformanceCalculated({ positions })),
          catchError(() => of(positionsActions.positionQuotesSubscriptionFailure())),
        ),
      ),
      catchError(() => of(positionsActions.positionQuotesSubscriptionFailure())),
    );
  });

  public unsubscribeFromPositionQuote$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(positionsActions.unsubscribeFromPositionQuote),
        tap(({ isin }) => {
          return this.pushQuotes.unsubscribe(isin);
        }),
      );
    },
    { dispatch: false },
  );

  public loadPositionNotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.loadPositionsNotes, positionsApiActions.loadPositionsSuccess),
      switchMap(() =>
        this.positionsInfrastructure.getNotes().pipe(
          map((notes) => positionsApiActions.loadPositionsNotesSuccess({ notes })),
          catchError((error) => of(positionsApiActions.loadPositionsNotesFailure({ error }))),
        ),
      ),
      catchError((error) => of(positionsApiActions.loadPositionsNotesFailure({ error }))),
    );
  });

  public loadPositionNoteByIsin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.loadPositionNoteByIsin),
      switchMap(({ isin }) =>
        this.positionsInfrastructure.getNoteByIsin(isin).pipe(
          map((note) => positionsApiActions.loadPositionNoteByIsinSuccess({ note })),
          catchError((error) => {
            if (error?.error?.errorCode === 'NOT_FOUND') {
              const note = { isin } as PositionNote;
              return of(positionsApiActions.loadPositionNoteByIsinSuccess({ note }));
            }

            return of(positionsApiActions.loadPositionNoteByIsinFailure({ error }));
          }),
        ),
      ),
      catchError((error) => of(positionsApiActions.loadPositionNoteByIsinFailure({ error }))),
    );
  });

  public updatePositionNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.updatePositionNote),
      switchMap(({ isin, note }) =>
        this.positionsInfrastructure.putPositionNoteByIsin(isin, note).pipe(
          map((note) => positionsApiActions.updatePositionNoteSuccess({ note })),
          catchError((error) => of(positionsApiActions.updatePositionNoteFailure({ error }))),
        ),
      ),
      catchError((error) => of(positionsApiActions.updatePositionNoteFailure({ error }))),
    );
  });

  public updatePositionEntryQuote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsActions.updatePositionEntryQuote),
      concatLatestFrom(({ isin }) => this.store.select(selectPositionByIsin(isin))),
      switchMap(([{ isin, entryQuote }, { avgEntryQuoteOriginal }]) =>
        this.positionsInfrastructure
          .postEntryQuotesEdit([
            {
              isin,
              newAvgEntryQuote: entryQuote !== 0 && !!entryQuote ? entryQuote : avgEntryQuoteOriginal,
            } as CustomEntryQuotesRequest,
          ])
          .pipe(
            map(() => ({ isin, entryQuote: entryQuote !== 0 && !!entryQuote ? entryQuote : avgEntryQuoteOriginal })),
            concatLatestFrom(({ isin }) => this.store.select(selectPositionByIsin(isin))),
            map(([{ entryQuote }, position]) => calculatePositionPerformance({ ...position, avgEntryQuote: entryQuote })),
            map((position) => positionsApiActions.updatePositionEntryQuoteSuccess({ position })),
            catchError((error) => of(positionsApiActions.updatePositionEntryQuoteFailure({ error }))),
          ),
      ),
      catchError((error) => of(positionsApiActions.updatePositionEntryQuoteFailure({ error }))),
    );
  });

  public setCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(positionsApiActions.loadPositionsSuccess),
      concatLatestFrom(() => this.store.select(positionsFeature.selectAll)),
      map(([, positions]) => positions),
      map((positions) => {
        const positionsByType = positions.reduce<Record<string, (Position | string)[]>>((acc, position) => {
          if (position.type && position.type !== InstrumentType.Crypto) {
            acc[position.type] = [...(acc[position.type] || []), position];
          }
          return acc;
        }, {});
        const categories = Object.keys(positionsByType);
        return categories;
      }),
      map((categories) => categories.sort(sortFilterCategories)),
      map((categories) => positionsActions.setPositionCategories({ categories })),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          positionsApiActions.loadPositionsFailure,
          positionsApiActions.loadPositionsNotesFailure,
          positionsApiActions.loadPositionNoteByIsinFailure,
          positionsApiActions.updatePositionNoteFailure,
          positionsApiActions.updatePositionEntryQuoteFailure,
        ),
        tap((action) => {
          this.storeErrorHandler.handleBackendError(action.error);
        }),
      );
    },
    { dispatch: false },
  );
}

const calculatePositionPerformance = (position: Position) => {
  const currentValue = position.quantity * (position.quote ?? position.avgEntryQuote);
  const entryValue = position.quantity * position.avgEntryQuote;
  const perfPct = ((currentValue - entryValue) / entryValue) * 100;
  const perfAbs = currentValue - entryValue;

  const perfTodayAbs = !position.historicQuote?.bid ? 0 : (position.quote - position.historicQuote.bid) * position.quantity;
  const perfTodayPct = !position.historicQuote?.bid
    ? 0
    : ((position.quote - position.historicQuote.bid) / position.historicQuote.bid) * 100;

  return { ...position, perfPct, perfAbs, perfTodayAbs, perfTodayPct, entryValue, currentValue };
};

const isSoldOut = (position: Position, quote: Quote) => {
  return position ? position.type === InstrumentType.Derivative && !position.knockedOutFlag && quote.ask === 0 && quote.bid > 0 : false;
};

const mapQuoteToPosition = (quotes: Record<string, Quote>, position: Position) => {
  const rawQuote = quotes[position.isin];

  if (!rawQuote) {
    return null;
  }

  if (rawQuote.time === position.quoteTime || !rawQuote.bid) {
    return null;
  }
  const quote = rawQuote.bid ?? 0;
  const quoteTime = rawQuote.time;

  const currentValue = position.quantity * quote;
  const entryValue = position.quantity * position.avgEntryQuote;
  const perfPct = ((currentValue - entryValue) / entryValue) * 100;
  const perfAbs = currentValue - entryValue;
  const historicQuote = position?.historicQuote?.bid ?? 0;
  const perfTodayAbs = historicQuote === 0 ? 0 : (quote - historicQuote) * position.quantity;
  const perfTodayPct = historicQuote === 0 ? 0 : ((quote - historicQuote) / historicQuote) * 100;
  const isTradable = !position.knockedOutFlag && !position.endfaellig;
  const isBuyable = isTradable && !isSoldOut(position, rawQuote);
  const isSellable = isTradable;
  const quotePerfTodayAbs = quote - historicQuote;

  return {
    ...position,
    rawQuote,
    quote,
    quoteTime,
    currentValue,
    entryValue,
    perfPct,
    perfAbs,
    perfTodayAbs,
    perfTodayPct,
    isBuyable,
    isSellable,
    quotePerfTodayAbs,
  } as Position;
};
